/**
 * @efacture/fiscal-core - Tax engine
 *
 * Ce module implémente le calcul des taxes pour le système de facturation
 * électronique certifiée du Burkina Faso conformément aux spécifications
 * de la DGI (Direction Générale des Impôts).
 *
 * Taxes applicables:
 * - TVA (Taxe sur la Valeur Ajoutée): 18% ou 10% selon le groupe fiscal
 * - TSB (Taxe Spéciale sur les Boissons): 10% sur les boissons (calculée séparément, pas dans les groupes de taxe)
 * - PSVB (Prélèvement à la Source sur Vente de Biens) - DGI section 7.7:
 *   - Groupe A: 2% (droit commun)
 *   - Groupe B: 1% (dérogation)
 *   - Groupe C: 0.2% (dérogation)
 *   - Groupe D: 0% (réservé)
 *
 * Base de calcul:
 * - La TSB est calculée sur le montant HT ; la TVA est calculée sur
 *   HT + TSB (CGI art. 311 §1 : la base d'imposition comprend « le montant
 *   des droits de douane, des droits d'accises et toute autre taxe
 *   applicables aux produits », TVA exceptée — DGI §6.9 dit la même chose)
 * - Le PSVB est calculé sur le montant TTC (DGI §10.6)
 * - TTC = HT + TVA + TSB (le PSVB est un prélèvement, pas ajouté au TTC)
 *
 * Amounts are computed per line to the centime (spec §6.2: nearest value,
 * two decimals at most) with `roundMoney`; group and invoice totals are sums
 * of the line values, as the MCF reports them on 33h.
 */
import { TAX_GROUPS, TAX_GROUP_CODES, PSVB_GROUPS, } from "../constants/tax-groups.js";
import { secefHtArticle, secefItemFigures, secefTtcTaxable } from "@efacture/mcf-protocol/secef-item-rule";
import { roundMoney, sumMoney } from "./money.js";
export { roundMoney, sumMoney };
/** Taux de TSB (Taxe Spéciale sur les Boissons) */
export const TSB_RATE = 0.10;
/**
 * PSVB of an amount: the amount TTC times the group rate, to the centime
 * (DGI §10.6). The levy of an invoice is taken once on its total, see
 * `allocatePsvb`.
 */
export function calculatePsvbAmount(totalTTC, psvbRate) {
    return psvbRate > 0 ? roundMoney(totalTTC * psvbRate) : 0;
}
/**
 * PSVB of each line of one invoice. The levy is taken once per rate on the
 * invoice total TTC of the lines at that rate (SFE spec §6.10, the MCF's
 * 33h MPSVB), then spread over those lines by largest remainder, in exact
 * centimes: each line gets the floor of its exact share, and the centimes
 * left to reach the levy go one each to the lines with the largest
 * fractional remainders (ties to the earlier line). Shares are never of the
 * opposite sign to their line and always sum to the levy. The lines of one
 * rate carry one sign (a sale or a credit note).
 */
export function allocatePsvb(lines) {
    const shares = lines.map(() => 0);
    const rates = new Set(lines.map((line) => line.psvbRate).filter((rate) => rate > 0));
    for (const rate of rates) {
        const indexes = lines.flatMap((line, index) => (line.psvbRate === rate ? [index] : []));
        const total = sumMoney(indexes.map((i) => lines[i].totalTTC));
        const sign = total < 0 ? -1 : 1;
        if (indexes.some((i) => sign * lines[i].totalTTC < 0)) {
            throw new Error("PSVB : lignes de signes opposés sur une même facture");
        }
        const levyCentimes = Math.round(Math.abs(calculatePsvbAmount(total, rate)) * 100);
        const ratePpm = BigInt(Math.round(rate * 1_000_000));
        const exact = indexes.map((i) => BigInt(Math.round(Math.abs(lines[i].totalTTC) * 100)) * ratePpm);
        const floors = exact.map((value) => Number(value / 1000000n));
        let left = levyCentimes - floors.reduce((sum, value) => sum + value, 0);
        const byRemainder = indexes
            .map((_, position) => position)
            .sort((a, b) => {
            const ra = exact[a] % 1000000n;
            const rb = exact[b] % 1000000n;
            return ra === rb ? a - b : rb > ra ? 1 : -1;
        });
        for (const position of byRemainder) {
            if (left <= 0)
                break;
            floors[position] = floors[position] + 1;
            left -= 1;
        }
        indexes.forEach((lineIndex, position) => {
            const share = floors[position] / 100;
            shares[lineIndex] = share === 0 ? 0 : sign * share;
        });
    }
    return shares;
}
/**
 * A proportional levy (TSB, ad valorem specific tax) on an HT, as an amount
 * per unit times the quantity: the MCF takes a specific tax per unit (31h
 * TSR, never a percent), and TSR × QT must give the levy exactly.
 */
function levyPerUnit(rate, ht, quantity) {
    return rate > 0 ? roundMoney(roundMoney((ht * rate) / quantity) * quantity) : 0;
}
/** A levy with the VAT the MCF adds to it (31h TS reply). */
export function levyWithVat(levy, tvaRate) {
    return roundMoney(levy * (1 + tvaRate));
}
/**
 * The amount per unit, in hundredths, of a levy of `amountH` hundredths over
 * a quantity of `quantityT` thousandths, when one exists: the MCF takes a
 * specific tax as TSR per unit and requires TSR × QT = TS exactly. `null`
 * when no amount to the centime per unit gives the levy.
 */
export function perUnitHundredths(amountH, quantityT) {
    if (!(quantityT > 0))
        return null;
    const scaled = amountH * 1000;
    return scaled % quantityT === 0 ? scaled / quantityT : null;
}
/** `value × factor` when it is an integer (to 1e-6), null otherwise. */
export function exactScaled(value, factor) {
    const scaled = value * factor;
    const rounded = Math.round(scaled);
    return Math.abs(scaled - rounded) <= 1e-6 ? rounded : null;
}
/**
 * HT line total with VAT, in hundredths, as the MCF computes the line's 31h
 * article (`secefHtArticle`, HT mode). Without discount: PR × QT × (1 + rate)
 * to the centime, PR the unit price. With a discount, sent with PRORIG:
 * G × (1 + rate) − D × (1 + rate), each to the centime, G the gross amount
 * and D the discount, so the VAT is r2(G × rate) − r2(D × rate). `null` when
 * an input is finer than the centime or the thousandth of a unit, or when a
 * discounted line's G is not a whole number of centimes (`mcfLineRefusal`
 * refuses that line at entry).
 */
function mcfHtLineTotalHundredths(unitPrice, quantity, discount, tvaRate) {
    const unitH = exactScaled(unitPrice, 100);
    const quantityT = exactScaled(quantity, 1000);
    const discountH = exactScaled(discount, 100);
    if (unitH === null || quantityT === null || discountH === null || quantityT <= 0)
        return null;
    const gross = BigInt(unitH) * BigInt(quantityT);
    const rate = Math.round(tvaRate * 10_000);
    if (discountH === 0)
        return secefHtArticle({ rate, gross });
    if (gross % 1000n !== 0n)
        return null;
    return secefHtArticle({ rate, gross, change: BigInt(discountH) * 1000n });
}
/** `value` as an integer over a power of ten, exact to six decimals. */
function decimalRatio(value) {
    for (let digits = 0; digits <= 6; digits++) {
        const scaled = exactScaled(value, 10 ** digits);
        if (scaled !== null)
            return { value: BigInt(scaled), divisor: 10n ** BigInt(digits) };
    }
    return { value: BigInt(Math.round(value * 1_000_000)), divisor: 1000000n };
}
/**
 * HT of a TTC-mode line as the MCF splits its 31h article
 * (`secefTtcTaxable`): the unit price / (1 + rate) to four decimals, times
 * the quantity, to the centime. A discounted line without levy goes with
 * PRORIG: the unit price is split, then the discount / (1 + rate) to the
 * centime comes off. Otherwise the unit price split is PR, the net amount
 * less `deducted` (a levy with its VAT) over the quantity.
 */
function mcfTtcTaxable(unitPrice, quantity, discount, deducted, tvaRate) {
    const rate = Math.round(tvaRate * 10_000);
    const price = decimalRatio(unitPrice);
    const qty = decimalRatio(quantity);
    const off = decimalRatio(discount);
    const levy = decimalRatio(deducted);
    const thousandths = { quantity: qty.value * 1000n, quantityDivisor: qty.divisor };
    if (discount > 0 && deducted === 0) {
        const taxable = secefTtcTaxable({
            rate,
            unitPrice: price.value * 100n,
            unitPriceDivisor: price.divisor,
            ...thousandths,
            change: off.value * 100000n,
            changeDivisor: off.divisor,
        });
        return taxable / 100;
    }
    // PR = (unit price × quantity − discount − deducted) / quantity, in hundredths.
    const net = price.value * qty.value * off.divisor * levy.divisor -
        off.value * price.divisor * qty.divisor * levy.divisor -
        levy.value * price.divisor * qty.divisor * off.divisor;
    const taxable = secefTtcTaxable({
        rate,
        unitPrice: net * 100n,
        unitPriceDivisor: price.divisor * off.divisor * levy.divisor * qty.value,
        ...thousandths,
    });
    return taxable / 100;
}
/**
 * The most a TTC-mode TSB or specific tax is raised, in centimes per unit,
 * to make its line certifiable (`certifiableLevy`).
 */
export const MAX_LEVY_RAISE_PER_UNIT_CENTIMES = 49;
/**
 * Whether the MCF certifies, as printed, a TTC-mode line without discount
 * carrying a levy of `levyH` hundredths (`secefItemFigures`): PR, the unit
 * price less the levy with its VAT over the quantity, must be to the centime
 * and give the article amount.
 */
function levyCertifies(unitH, quantityT, totalH, levyH, tvaRate) {
    const levyWithVatH = Math.round(levyWithVat(levyH / 100, tvaRate) * 100);
    const net = unitH * quantityT - levyWithVatH * 1000;
    if (net <= 0 || net % quantityT !== 0)
        return false;
    const figures = secefItemFigures({
        priceMode: "TTC",
        rate: Math.round(tvaRate * 10_000),
        unitPrice: net / quantityT,
        quantity: quantityT,
        amount: totalH,
        specificTax: levyH,
    });
    return figures !== null;
}
/**
 * The levy, in hundredths, the MCF certifies a TTC-mode line without
 * discount with: `levyH` when it does, else that levy raised per unit one
 * centime at a time, by at most `MAX_LEVY_RAISE_PER_UNIT_CENTIMES` (SFE
 * §6.7: the tax rounded up so that HT plus tax gives the total). Null when
 * no levy within that raise does, or when the line is not to the centime
 * and the thousandth of a unit.
 */
export function certifiableLevy(unitPrice, quantity, totalTTC, levyH, tvaRate) {
    const unitH = exactScaled(unitPrice, 100);
    const quantityT = exactScaled(quantity, 1000);
    const totalH = exactScaled(totalTTC, 100);
    if (unitH === null || quantityT === null || totalH === null || quantityT <= 0)
        return null;
    const perUnitH = perUnitHundredths(levyH, quantityT);
    if (perUnitH === null)
        return null;
    for (let raise = 0; raise <= MAX_LEVY_RAISE_PER_UNIT_CENTIMES; raise++) {
        const levy = (perUnitH + raise) * quantityT;
        if (levy % 1000 !== 0)
            continue;
        if (levyCertifies(unitH, quantityT, totalH, levy / 1000, tvaRate))
            return levy / 1000;
    }
    return null;
}
/** A number with a decimal comma, as the refusals print it. */
function frenchNumber(value, decimals) {
    return (decimals === undefined ? String(value) : value.toFixed(decimals)).replace(".", ",");
}
/**
 * Whether a TSB or specific tax is given by an amount per unit to the
 * centime over the line's quantity (see `perUnitHundredths`). Read on the
 * magnitude, so a credit note's negative levy follows the same rule.
 */
export function levyDividesPerUnit(levy, quantity) {
    return (levy === 0 ||
        perUnitHundredths(Math.round(Math.abs(levy) * 100), Math.round(quantity * 1000)) !== null);
}
/** Refuses a levy `levyDividesPerUnit` rejects, naming it with `label`. */
function assertLevySplitsPerUnit(levy, quantity, label) {
    if (!levyDividesPerUnit(levy, quantity)) {
        throw new Error(`${label} de ${frenchNumber(levy, 2)} F ne se répartit pas au centime sur la quantité ` +
            `${frenchNumber(quantity)} : le MCF exige un montant exact par unité. Modifiez la quantité.`);
    }
}
/** Steps of the TTC-mode HT / levy iteration before an alternation is resolved. */
const TTC_SPLIT_PASSES = 5;
/**
 * Specific tax parameters the calculator accepts: a positive rate at most 1
 * for AD_VALOREM, a positive unit amount for FIXED_PER_UNIT.
 */
function assertSpecificTaxParams(params) {
    if (params.mode === "AD_VALOREM") {
        if (!(params.rate > 0 && params.rate <= 1)) {
            throw new Error("Le taux de la taxe spécifique doit être une fraction comprise entre 0 exclu et 1 inclus");
        }
        return;
    }
    if (!(params.unitAmount > 0)) {
        throw new Error("Le montant forfaitaire de la taxe spécifique doit être supérieur à zéro");
    }
}
/**
 * Calcule les taxes pour une ligne de facture.
 *
 * Règles de calcul selon la DGI Burkina Faso:
 * 1. Calcul du montant HT: (prix unitaire × quantité) - remise
 * 2. Application de la TVA selon le groupe fiscal (18%, 10% ou 0%)
 * 3. Application de la TSB si l'article est une boisson (10%)
 * 4. Application du PSVB si groupe PSVB fourni (sur le TTC — DGI §10.6):
 *    - Groupe A: 2% (droit commun)
 *    - Groupe B: 1% (dérogation)
 *    - Groupe C: 0.2% (dérogation)
 *    - Groupe D: 0% (réservé)
 * 5. TTC = HT + TVA + TSB (le PSVB est un prélèvement séparé)
 *
 * @param line - Paramètres de la ligne (prix unitaire, quantité, remise, groupe fiscal, groupe PSVB optionnel)
 * @returns Calcul fiscal complet de la ligne
 *
 * @example
 * ```typescript
 * // Bien taxable simple (TVA 18%)
 * const result = calculateLineTax({
 *   unitPrice: 10000,
 *   quantity: 2,
 *   discount: 0,
 *   taxGroup: "B"
 * });
 * // result.netHT = 20000
 * // result.taxes.tvaAmount = 3600 (18%)
 * // result.lineTotalTTC = 23600
 *
 * // Boisson avec TVA et TSB (TVA 18% + TSB 10% + PSVB 2%)
 * const beverage = calculateLineTax({
 *   unitPrice: 5000,
 *   quantity: 10,
 *   discount: 5000,
 *   taxGroup: "B",
 *   isBeverage: true,
 *   psvbGroup: "A"
 * });
 * // beverage.netHT = 45000
 * // beverage.taxes.tvaAmount = 8100 (18%)
 * // beverage.taxes.tsbAmount = 4500 (10%)
 * // beverage.taxes.psvbAmount = 1152 (2% sur TTC — DGI §10.6: 57600 × 0.02)
 * // beverage.lineTotalTTC = 57600 (HT + TVA + TSB; PSVB est un prelevement separe)
 * ```
 */
/**
 * @deprecated This function is deprecated and will be removed in a future version.
 * It exists only for backward compatibility with tests.
 * Line numbers should be passed explicitly via the lineNumber parameter.
 */
export function resetLineCounter() {
    // No-op: global line counter has been removed.
    // This function is kept for backward compatibility but does nothing.
}
export function calculateLineTax(line) {
    // Use provided lineNumber or default to 1
    // Caller is responsible for tracking line numbers across multiple calls
    const effectiveLineNumber = line.lineNumber ?? 1;
    const { unitPrice, quantity, discount, taxGroup, isBeverage, psvbGroup, priceMode = "HT", specificTax = null, } = line;
    // Validation: la quantite doit etre superieure a zero
    if (quantity <= 0) {
        throw new Error("La quantité doit être supérieure à zéro");
    }
    // Validation: le prix unitaire doit etre strictement positif
    if (unitPrice <= 0) {
        throw new Error("Le prix unitaire doit être supérieur à zéro");
    }
    // Validation: la remise ne peut pas etre negative ni superieure au montant brut
    if (discount < 0) {
        throw new Error("La remise ne peut pas être négative");
    }
    if (discount >= unitPrice * quantity) {
        throw new Error("La remise ne peut pas être supérieure ou égale au montant brut de la ligne");
    }
    // Récupérer les taux de taxe du groupe fiscal
    const taxGroupConfig = TAX_GROUPS[taxGroup];
    const tvaRate = taxGroupConfig.tvaRate;
    // TSB appliquée si l'article est une boisson (non déterminé par le groupe fiscal)
    const tsbRate = isBeverage ? TSB_RATE : 0;
    // Specific tax (DGI §2.17): one of the two modes per article, both zero
    // when the line carries none.
    if (specificTax !== null) {
        assertSpecificTaxParams(specificTax);
    }
    const adValoremRate = specificTax?.mode === "AD_VALOREM" ? specificTax.rate : 0;
    const fixedSpecificTax = specificTax?.mode === "FIXED_PER_UNIT" ? specificTax.unitAmount * quantity : 0;
    // Validation: verifier que le groupe PSVB est valide
    if (psvbGroup && !(psvbGroup in PSVB_GROUPS)) {
        throw new Error(`Groupe PSVB invalide: ${psvbGroup}`);
    }
    // Récupérer le taux PSVB si applicable
    const psvbRate = psvbGroup ? PSVB_GROUPS[psvbGroup].rate : 0;
    let netHT;
    let tvaAmount;
    let tsbAmount;
    let specificTaxAmount;
    let totalTTC;
    if (priceMode === "TTC") {
        // Mode TTC: prix unitaire toutes taxes comprises
        const grossTTC = unitPrice * quantity;
        totalTTC = roundMoney(grossTTC - discount);
        if (tvaRate === 0 && tsbRate === 0 && adValoremRate === 0 && fixedSpecificTax === 0) {
            // Pas de taxe: HT = TTC
            netHT = totalTTC;
            tvaAmount = 0;
            tsbAmount = 0;
            specificTaxAmount = 0;
        }
        else if (tsbRate === 0 && adValoremRate === 0 && fixedSpecificTax === 0) {
            // VAT only: the MCF's split of the line's 31h article (MH), the VAT
            // the remainder.
            netHT = mcfTtcTaxable(unitPrice, quantity, discount, 0, tvaRate);
            tvaAmount = roundMoney(totalTTC - netHT);
            tsbAmount = 0;
            specificTaxAmount = 0;
        }
        else {
            // The MCF takes the TSB / specific tax before VAT and adds its VAT
            // itself (31h TS reply = TS × (1 + v)); the rest of the price is the
            // article amount, split like any TTC amount. HT, the levy and the
            // levy's VAT depend on one another, so the split is iterated from the
            // inverse formula HT = (TTC / (1 + v) − f·q) / (1 + t + a) until HT
            // no longer moves. Every step is MCF-consistent: its HT is the MCF's
            // split of the price left once the levy with its VAT is taken out.
            const articleTaxable = (levy) => mcfTtcTaxable(unitPrice, quantity, discount, levyWithVat(levy, tvaRate), tvaRate);
            const splitFrom = (ht) => {
                const tsb = levyPerUnit(tsbRate, ht, quantity);
                const specific = roundMoney(levyPerUnit(adValoremRate, ht, quantity) + fixedSpecificTax);
                return { from: ht, tsb, specific, netHT: articleTaxable(roundMoney(tsb + specific)) };
            };
            let split = splitFrom(roundMoney((totalTTC / (1 + tvaRate) - fixedSpecificTax) / (1 + tsbRate + adValoremRate)));
            for (let pass = 1; pass < TTC_SPLIT_PASSES && split.netHT !== split.from; pass++) {
                split = splitFrom(split.netHT);
            }
            // Without a fixed point the iteration alternates between two HT
            // values, each giving the levy computed from the other. The step kept
            // is the one reached after TTC_SPLIT_PASSES, unless only the other
            // one's levy divides exactly per unit, which the MCF requires.
            if (split.netHT !== split.from) {
                const other = splitFrom(split.netHT);
                const alternates = other.netHT === split.from;
                const exact = (step) => levyDividesPerUnit(step.tsb, quantity) && levyDividesPerUnit(step.specific, quantity);
                if (alternates && !exact(split) && exact(other)) {
                    split = other;
                }
            }
            // A levy the MCF does not certify the line with is raised per unit
            // until it does; the raise goes to the TSB when the line carries one.
            if (discount === 0) {
                const levyH = Math.round(roundMoney(split.tsb + split.specific) * 100);
                const raisedH = certifiableLevy(unitPrice, quantity, totalTTC, levyH, tvaRate);
                if (raisedH !== null && raisedH !== levyH) {
                    const raise = (raisedH - levyH) / 100;
                    const tsb = split.tsb > 0 ? roundMoney(split.tsb + raise) : split.tsb;
                    const specific = split.tsb > 0 ? split.specific : roundMoney(split.specific + raise);
                    split = { from: split.from, tsb, specific, netHT: articleTaxable(raisedH / 100) };
                }
            }
            tsbAmount = split.tsb;
            specificTaxAmount = split.specific;
            netHT = split.netHT;
            tvaAmount = roundMoney(totalTTC - netHT - tsbAmount - specificTaxAmount);
            // A line carrying a specific tax must keep a positive stored HT: the
            // levy with its VAT can reach the whole price.
            if (specificTax !== null && netHT <= 0) {
                throw new Error(fixedSpecificTax > 0
                    ? "Le forfait de taxe spécifique atteint ou dépasse le prix TTC de la ligne"
                    : "La taxe spécifique et la TVA atteignent ou dépassent le prix TTC de la ligne");
            }
        }
    }
    else {
        // Mode HT (par défaut): prix unitaire hors taxes
        // Formule DGI §10.2: TVA = montant_imposable × taux / 100
        const grossHT = unitPrice * quantity;
        netHT = roundMoney(grossHT - discount);
        // TSB and specific tax on the HT, per unit; the VAT is that of the HT
        // plus that of the levy, each rounded as the MCF rounds them (VAT on
        // HT + TSB + specific tax, CGI art. 311 §1, DGI §6.9). Without levy,
        // a line the MCF receives with its quantity has the MCF's total
        // (`mcfHtLineTotalHundredths`) and its VAT is the rest.
        tsbAmount = levyPerUnit(tsbRate, netHT, quantity);
        specificTaxAmount = roundMoney(levyPerUnit(adValoremRate, netHT, quantity) + fixedSpecificTax);
        const levy = roundMoney(tsbAmount + specificTaxAmount);
        const mcfTotalH = levy === 0 ? mcfHtLineTotalHundredths(unitPrice, quantity, discount, tvaRate) : null;
        tvaAmount =
            mcfTotalH !== null
                ? roundMoney(mcfTotalH / 100 - netHT)
                : roundMoney(roundMoney(netHT * tvaRate) + levyWithVat(levy, tvaRate) - levy);
        // TTC = HT + TVA + TSB + specific tax (PSVB is a withholding, outside the TTC)
        totalTTC = roundMoney(netHT + tvaAmount + tsbAmount + specificTaxAmount);
    }
    if (line.exactLevyPerUnit !== false) {
        assertLevySplitsPerUnit(tsbAmount, quantity, "La TSB");
        assertLevySplitsPerUnit(specificTaxAmount, quantity, "La taxe spécifique");
    }
    // PSVB on the TTC amount (DGI §10.6).
    const psvbAmount = calculatePsvbAmount(totalTTC, psvbRate);
    const taxes = {
        baseHT: netHT,
        tvaBase: roundMoney(netHT + tsbAmount + specificTaxAmount),
        tvaAmount,
        tvaRate,
        tsbAmount,
        tsbRate,
        specificTaxAmount,
        specificTax,
        psvbAmount,
        psvbRate,
        totalTTC,
    };
    return {
        lineNumber: effectiveLineNumber,
        unitPriceHT: priceMode === "TTC" ? netHT / quantity : unitPrice,
        quantity,
        discountAmount: discount,
        netHT,
        taxes,
        lineTotalTTC: totalTTC,
        taxGroup,
    };
}
function gcd(a, b) {
    let x = a < 0n ? -a : a;
    let y = b < 0n ? -b : b;
    while (y !== 0n)
        [x, y] = [y, x % y];
    return x === 0n ? 1n : x;
}
function exact(numerator, denominator = 1n) {
    const sign = denominator < 0n ? -1n : 1n;
    const divisor = gcd(numerator, denominator);
    return { numerator: (sign * numerator) / divisor, denominator: (sign * denominator) / divisor };
}
function exactOf(value) {
    const { value: numerator, divisor } = decimalRatio(Math.abs(value));
    return exact(value < 0 ? -numerator : numerator, divisor);
}
const exactAdd = (a, b) => exact(a.numerator * b.denominator + b.numerator * a.denominator, a.denominator * b.denominator);
const exactSub = (a, b) => exact(a.numerator * b.denominator - b.numerator * a.denominator, a.denominator * b.denominator);
const exactMul = (a, b) => exact(a.numerator * b.numerator, a.denominator * b.denominator);
const exactDiv = (a, b) => exact(a.numerator * b.denominator, a.denominator * b.numerator);
/**
 * The amounts `calculateLineTax` computes for a line, by the same formula
 * with no rounding at any step: what the MCF's rounding of the line departs
 * from. HT mode: HT = quantity × price − discount, the TSB and the specific
 * tax on it, the VAT on HT + TSB + specific tax. TTC mode: TTC = quantity ×
 * price − discount, the VAT base TTC / (1 + VAT rate), split between HT and
 * levies by HT = (base − fixed tax × quantity) / (1 + TSB rate + ad valorem
 * rate). The PSVB is the TTC times its rate.
 */
export function exactLineAmounts(line) {
    const quantity = exactOf(line.quantity);
    const entered = exactSub(exactMul(exactOf(line.unitPrice), quantity), exactOf(line.discount));
    const vat = exactOf(line.tvaRate);
    const tsbRate = exactOf(line.isBeverage ? TSB_RATE : 0);
    const specificTax = line.specificTax ?? null;
    const adValorem = exactOf(specificTax?.mode === "AD_VALOREM" ? specificTax.rate : 0);
    const fixed = exactMul(exactOf(specificTax?.mode === "FIXED_PER_UNIT" ? specificTax.unitAmount : 0), quantity);
    const one = exact(1n);
    let netHT;
    let tvaBase;
    if (line.priceMode === "TTC") {
        tvaBase = exactDiv(entered, exactAdd(one, vat));
        netHT = exactDiv(exactSub(tvaBase, fixed), exactAdd(one, exactAdd(tsbRate, adValorem)));
    }
    else {
        netHT = entered;
        tvaBase = exactAdd(exactMul(netHT, exactAdd(one, exactAdd(tsbRate, adValorem))), fixed);
    }
    const tsb = exactMul(netHT, tsbRate);
    const specific = exactAdd(exactMul(netHT, adValorem), fixed);
    const ttc = line.priceMode === "TTC" ? entered : exactMul(tvaBase, exactAdd(one, vat));
    return {
        netHT,
        tsb,
        specificTax: specific,
        tvaBase,
        tva: exactSub(ttc, tvaBase),
        ttc,
        psvb: exactMul(ttc, exactOf(line.psvbRate ?? 0)),
    };
}
const NO_ERROR = exact(0n);
/** The most a rounding to the centime moves an amount. */
const HALF_CENTIME = exact(1n, 200n);
/** The most the MCF's split of a TTC unit price to four decimals moves it. */
const HALF_TEN_THOUSANDTH = exact(1n, 20000n);
/** The most a PSVB share moves from its line's TTC times the rate (`allocatePsvb`). */
const ONE_CENTIME = exact(1n, 100n);
const isCentime = (amount) => (amount.numerator * 100n) % amount.denominator === 0n;
const exactMax = (a, b) => a.numerator * b.denominator >= b.numerator * a.denominator ? a : b;
const exactSum = (...amounts) => amounts.reduce(exactAdd, NO_ERROR);
/**
 * The most `calculateLineTax`, `genericLineTax` and `allocatePsvb` can move
 * each amount of a line from `exactLineAmounts`: 0,005 per rounding to the
 * centime (the entered amount's only when not to the centime), once per
 * unit started for a rounding per unit, 0,00005 per unit started for the
 * MCF's four-decimal TTC split, 0,01 for a PSVB share; an error carried
 * into a product counts times its factor.
 */
export function lineRoundingBounds(line) {
    const quantity = exactOf(line.quantity);
    const entered = exactSub(exactMul(exactOf(line.unitPrice), quantity), exactOf(line.discount));
    const enteredStep = isCentime(entered) ? NO_ERROR : HALF_CENTIME;
    const unitsStarted = exact((quantity.numerator + quantity.denominator - 1n) / quantity.denominator);
    const perUnitRounding = exactMul(unitsStarted, HALF_CENTIME);
    const vat = exactOf(line.tvaRate);
    const one = exact(1n);
    const discounted = line.discount > 0;
    const specificTax = line.specificTax ?? null;
    const tsbRate = line.isBeverage ? TSB_RATE : 0;
    const adValorem = specificTax?.mode === "AD_VALOREM" ? specificTax.rate : 0;
    const fixed = specificTax?.mode === "FIXED_PER_UNIT" && specificTax.unitAmount > 0
        ? exactMul(exactOf(specificTax.unitAmount), quantity)
        : null;
    const levied = tsbRate > 0 || adValorem > 0 || fixed !== null;
    const psvbOf = (ttc) => (line.psvbRate ?? 0) > 0 ? exactAdd(ONE_CENTIME, exactMul(exactOf(line.psvbRate), ttc)) : NO_ERROR;
    if (line.priceMode === "TTC") {
        const split = exactAdd(exactMul(unitsStarted, HALF_TEN_THOUSANDTH), HALF_CENTIME);
        // The levy's VAT, or the discount's split when the article goes with PRORIG (no levy left).
        const tva = exactSum(enteredStep, split, levied || discounted ? HALF_CENTIME : NO_ERROR);
        return { tsb: null, specificTax: null, tva, ttc: enteredStep, psvb: psvbOf(enteredStep) };
    }
    // levyPerUnit: the HT's error times the rate, the amount per unit, the product unless the quantity is whole.
    const wholeQuantity = quantity.denominator === 1n;
    const perUnitLevy = (rate) => rate > 0
        ? exactSum(exactMul(exactOf(rate), enteredStep), perUnitRounding, wholeQuantity ? NO_ERROR : HALF_CENTIME)
        : NO_ERROR;
    const tsb = perUnitLevy(tsbRate);
    const specific = exactAdd(perUnitLevy(adValorem), fixed !== null && !isCentime(fixed) ? HALF_CENTIME : NO_ERROR);
    const levy = exactAdd(tsb, specific);
    const article = discounted ? exactAdd(HALF_CENTIME, HALF_CENTIME) : HALF_CENTIME;
    const noLevyTva = exactMax(exactAdd(article, enteredStep), exactAdd(exactMul(vat, enteredStep), HALF_CENTIME));
    const noLevyTtc = exactMax(article, exactAdd(exactMul(exactAdd(one, vat), enteredStep), HALF_CENTIME));
    const both = exactAdd(enteredStep, levy);
    const tva = levied ? exactMax(exactSum(exactMul(vat, both), HALF_CENTIME, HALF_CENTIME), noLevyTva) : noLevyTva;
    const ttc = levied
        ? exactMax(exactSum(exactMul(exactAdd(one, vat), both), HALF_CENTIME, HALF_CENTIME), noLevyTtc)
        : noLevyTtc;
    return { tsb, specificTax: specific, tva, ttc, psvb: psvbOf(ttc) };
}
/**
 * The most the PSVB `allocatePsvb` levies on `lines` of one document, the
 * sum of their shares, moves from the sum of their `exactLineAmounts`
 * PSVB: 0,005 for the levy's rounding to the centime at each rate, plus
 * each line's TTC bound times its rate. The spread of a levy over its
 * lines rounds nothing: the shares sum to the levy.
 */
export function psvbLevyRoundingBound(lines) {
    const levied = lines.filter((line) => (line.psvbRate ?? 0) > 0);
    const rates = new Set(levied.map((line) => line.psvbRate));
    return exactSum(exactMul(exact(BigInt(rates.size)), HALF_CENTIME), ...levied.map((line) => exactMul(exactOf(line.psvbRate), lineRoundingBounds(line).ttc)));
}
/**
 * Extract or calculate HT/TVA/TTC for an InvoiceLine.
 *
 * Single source of truth: prefers stored values on the line (set at invoice creation).
 * Falls back to calculateLineTax() for draft invoices or test data without stored totals.
 *
 * All consumers that need line-level totals MUST use this function
 * instead of reimplementing the calculation.
 */
export function getLineTotals(line, priceMode = "HT", lineNumber = 1) {
    // Stored values take precedence (single source of truth)
    if (line.totalHT !== undefined && line.totalTTC !== undefined) {
        // Stored TSB and specific tax (DGI §6.8) both sit inside the stored TTC.
        // Without them the consumer cannot print either tax and the document
        // does not add up: HT + TVA falls short of the TTC by their exact amount.
        const tsb = line.totalTSB ?? 0;
        const specificTax = line.totalSpecificTax ?? 0;
        const tva = line.totalTVA !== undefined
            ? line.totalTVA
            : roundMoney(line.totalTTC - line.totalHT - tsb - specificTax);
        return { ht: line.totalHT, tva, tsb, specificTax, ttc: line.totalTTC, psvb: 0 };
    }
    // Fallback: calculate from raw inputs
    const calc = calculateLineTax({
        unitPrice: line.unitPrice,
        quantity: line.quantity,
        discount: line.discount,
        taxGroup: line.taxGroup,
        priceMode,
        lineNumber,
        ...(line.psvbGroup !== undefined && { psvbGroup: line.psvbGroup }),
        ...(line.isBeverage !== undefined && { isBeverage: line.isBeverage }),
        ...(line.specificTax !== undefined && { specificTax: line.specificTax }),
        ...(line.exactLevyPerUnit !== undefined && { exactLevyPerUnit: line.exactLevyPerUnit }),
    });
    return {
        ht: calc.netHT,
        tva: calc.taxes.tvaAmount,
        tsb: calc.taxes.tsbAmount,
        specificTax: calc.taxes.specificTaxAmount,
        ttc: calc.taxes.totalTTC,
        psvb: calc.taxes.psvbAmount,
    };
}
/**
 * Calcule la synthèse fiscale d'une facture à partir des lignes calculées.
 *
 * Cette fonction agrège les calculs de toutes les lignes pour produire:
 * - Les totaux globaux (HT, TVA, TSB, PSVB, TTC)
 * - Une ventilation par groupe de taxe
 *
 * @param lines - Tableau des calculs de lignes (résultat de calculateLineTax)
 * @returns Synthèse fiscale complète de la facture
 *
 * @example
 * ```typescript
 * const lines = [
 *   calculateLineTax({ unitPrice: 10000, quantity: 2, discount: 0, taxGroup: "B" }),
 *   calculateLineTax({ unitPrice: 5000, quantity: 3, discount: 0, taxGroup: "B", isBeverage: true, psvbGroup: "A" }),
 * ];
 *
 * const summary = calculateInvoiceTaxes(lines);
 * // summary.totalHT = 35000
 * // summary.totalTVA = 6300
 * // summary.totalTSB = 1500
 * // summary.totalPSVB = 300
 * // summary.totalTTC = 42800
 * ```
 */
export function calculateInvoiceTaxes(lines) {
    const byTaxGroup = Object.fromEntries(TAX_GROUP_CODES.map((group) => [
        group,
        {
            baseHT: 0,
            tvaBase: 0,
            tvaAmount: 0,
            tvaRate: TAX_GROUPS[group].tvaRate,
            tsbAmount: 0,
            tsbRate: 0,
            specificTaxAmount: 0,
            // Parameters differ from one article to the next; the per-group
            // aggregate carries the amount only, each line keeps its own params.
            specificTax: null,
            psvbAmount: 0,
            psvbRate: 0,
            totalTTC: 0,
        },
    ]));
    let totalHT = 0;
    let totalDiscount = 0;
    let totalTVA = 0;
    let totalTSB = 0;
    let totalSpecificTax = 0;
    let totalPSVB = 0;
    let totalTTC = 0;
    // Every total is the sum of the line values, kept at the centime. The
    // PSVB is summed as the lines carry it: pass freshly computed lines
    // through `withInvoicePsvb` first.
    const add = (total, amount) => roundMoney(total + amount);
    for (const line of lines) {
        totalHT = add(totalHT, line.netHT);
        totalDiscount = add(totalDiscount, line.discountAmount);
        totalTVA = add(totalTVA, line.taxes.tvaAmount);
        totalTSB = add(totalTSB, line.taxes.tsbAmount);
        totalSpecificTax = add(totalSpecificTax, line.taxes.specificTaxAmount);
        totalPSVB = add(totalPSVB, line.taxes.psvbAmount);
        totalTTC = add(totalTTC, line.lineTotalTTC);
        const group = byTaxGroup[line.taxGroup];
        group.baseHT = add(group.baseHT, line.netHT);
        group.tvaBase = add(group.tvaBase, line.taxes.tvaBase);
        group.tvaAmount = add(group.tvaAmount, line.taxes.tvaAmount);
        group.tsbAmount = add(group.tsbAmount, line.taxes.tsbAmount);
        group.specificTaxAmount = add(group.specificTaxAmount, line.taxes.specificTaxAmount);
        // Taux de TSB du groupe : porté dès qu'une ligne du groupe est une
        // boisson. La TSB n'est pas déterminée par le groupe de taxation mais
        // par l'article (isBeverage), et le taux est unique (TSB_RATE) — sans
        // cette reprise, le récapitulatif fiscal imprimerait « 4 500 (0 %) ».
        // Non-zero, not positive: a credit note carries a negative stored TSB.
        if (line.taxes.tsbAmount !== 0) {
            group.tsbRate = line.taxes.tsbRate;
        }
        group.psvbAmount = add(group.psvbAmount, line.taxes.psvbAmount);
        group.totalTTC = add(group.totalTTC, line.lineTotalTTC);
    }
    return {
        totalHT,
        totalDiscount,
        byTaxGroup,
        totalTVA,
        totalTSB,
        totalSpecificTax,
        totalPSVB,
        totalTTC,
    };
}
/**
 * The lines of one invoice with their PSVB replaced by their share of the
 * invoice-level levy (`allocatePsvb`).
 */
export function withInvoicePsvb(lines) {
    const psvb = allocatePsvb(lines.map((line) => ({ totalTTC: line.lineTotalTTC, psvbRate: line.taxes.psvbRate })));
    return lines.map((line, index) => ({ ...line, taxes: { ...line.taxes, psvbAmount: psvb[index] } }));
}
/**
 * PSVB levied on a sale from its stored lines: each line's group rate, the
 * levy taken once per rate on the lines' total (`allocatePsvb`), summed. A
 * line without a known group bears none.
 */
export function salePsvbTotal(lines) {
    const ordered = [...lines].sort((a, b) => a.lineNumber - b.lineNumber);
    return sumMoney(allocatePsvb(ordered.map((line) => ({
        totalTTC: line.totalTTC,
        psvbRate: line.psvbGroup && line.psvbGroup in PSVB_GROUPS
            ? PSVB_GROUPS[line.psvbGroup].rate
            : 0,
    }))));
}
//# sourceMappingURL=tax-calculator.js.map